import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { useAuth } from "@/context/AuthContext";
import { ShieldAlert, ArrowLeft, Users, GraduationCap, Clock, Mail, KeyRound, ClipboardList } from "lucide-react";

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso).slice(0, 10);
  }
};

const daysSince = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso).getTime();
    if (isNaN(d)) return null;
    return Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24));
  } catch {
    return null;
  }
};

const EVENT_LABELS = {
  password_reset_pm: "PM reset a learner password",
  password_reset_super: "Super admin reset a password",
  login: "Sign-in",
  register: "Registered",
  course_completed: "Completed course",
  quiz_passed: "Passed quiz",
  sign_off: "PM signed off learner",
  confidence_submitted: "Learner submitted confidence",
};

const prettyEvent = (t) => EVENT_LABELS[t] || (t || "event").replace(/_/g, " ");

export default function SuperAdminPMDetail() {
  const { practiceId } = useParams();
  const { isSuperAdmin, loading } = useAuth();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isSuperAdmin || !practiceId) return;
    let cancel = false;
    (async () => {
      setErr("");
      try {
        const res = await api.get(`/superadmin/practices/${practiceId}/detail`);
        if (!cancel) setData(res.data);
      } catch (e) {
        if (!cancel) setErr(e.response?.data?.detail || "Could not load practice detail");
      }
    })();
    return () => {
      cancel = true;
    };
  }, [isSuperAdmin, practiceId]);

  const sortedLearners = useMemo(() => {
    if (!data?.learners) return [];
    return [...data.learners].sort((a, b) => (b.last_login_at || "").localeCompare(a.last_login_at || ""));
  }, [data]);

  const sortedPms = useMemo(() => {
    if (!data?.pms) return [];
    return [...data.pms].sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
  }, [data]);

  if (loading) return null;
  if (!isSuperAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <Layout wide>
      <div className="mb-6">
        <Link
          to="/super/pms"
          data-testid="super-pm-detail-back"
          className="inline-flex items-center gap-1.5 text-sm text-brand-secondary hover:text-brand-ink transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
          Back to Practice Managers
        </Link>
      </div>

      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-brand-muted mb-1 flex items-center gap-1.5">
          <ShieldAlert className="w-3.5 h-3.5" strokeWidth={1.5} /> Super admin · Practice detail
        </div>
        <h1 className="font-serif text-4xl text-brand-ink" data-testid="super-pm-detail-title">
          {data?.practice?.name || (err ? "—" : "Loading…")}
        </h1>
        {data?.practice?.invite_code && (
          <div className="mt-2 text-sm text-brand-muted font-mono">
            Invite code: <span className="text-brand-ink">{data.practice.invite_code}</span>
          </div>
        )}
        {data?.practice?.created_at && (
          <div className="text-xs text-brand-muted mt-1">
            Practice created {fmt(data.practice.created_at)}
          </div>
        )}
      </div>

      {err && (
        <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700" data-testid="super-pm-detail-error">
          {err}
        </div>
      )}

      {data && (
        <div className="grid gap-6 lg:grid-cols-3 mb-8">
          <StatCard icon={<Users className="w-4 h-4" strokeWidth={1.5} />} label="Practice Managers" value={data.counts.pms} testId="super-pm-detail-stat-pms" />
          <StatCard icon={<GraduationCap className="w-4 h-4" strokeWidth={1.5} />} label="Learners" value={data.counts.learners} testId="super-pm-detail-stat-learners" />
          <StatCard icon={<ClipboardList className="w-4 h-4" strokeWidth={1.5} />} label="Recent events" value={data.counts.events} testId="super-pm-detail-stat-events" />
        </div>
      )}

      {data && (
        <>
          <SectionTitle>Practice Managers</SectionTitle>
          <UserTable rows={sortedPms} testIdPrefix="super-pm-detail-pm" emptyLabel="No Practice Managers." />

          <SectionTitle>Learners</SectionTitle>
          <UserTable rows={sortedLearners} testIdPrefix="super-pm-detail-learner" emptyLabel="No learners have joined this practice yet." showCompletions />

          <SectionTitle>Recent audit-log events</SectionTitle>
          {data.events.length === 0 ? (
            <div className="rounded-2xl border border-brand-linen bg-white p-6 text-center text-brand-muted text-sm">
              No audit-log events recorded for this practice yet.
            </div>
          ) : (
            <div className="rounded-2xl border border-brand-linen bg-white overflow-hidden">
              {data.events.map((e) => (
                <div
                  key={e.id}
                  data-testid={`super-pm-detail-event-${e.id}`}
                  className="border-b border-brand-linen last:border-b-0 px-5 py-3 flex flex-wrap items-baseline gap-x-4 gap-y-1 text-sm"
                >
                  <div className="text-brand-ink font-medium">{prettyEvent(e.event_type)}</div>
                  {e.actor?.email && (
                    <div className="text-brand-secondary text-xs">
                      by <span className="text-brand-ink">{e.actor.name || e.actor.email}</span>
                      {e.actor.role && <span className="text-brand-muted"> · {e.actor.role}</span>}
                    </div>
                  )}
                  {e.subject?.email && (
                    <div className="text-brand-secondary text-xs">
                      → <span className="text-brand-ink">{e.subject.name || e.subject.email}</span>
                    </div>
                  )}
                  {e.course_slug && (
                    <div className="text-brand-muted text-xs font-mono">{e.course_slug}</div>
                  )}
                  <div className="ml-auto text-brand-muted text-xs whitespace-nowrap">
                    {fmt(e.created_at)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Layout>
  );
}

function StatCard({ icon, label, value, testId }) {
  return (
    <div
      className="rounded-2xl border border-brand-linen bg-white p-5 flex items-start gap-3"
      data-testid={testId}
    >
      <span className="mt-0.5 rounded-full bg-brand-linen p-2 text-brand-ink">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-brand-muted">{label}</div>
        <div className="font-serif text-3xl text-brand-ink mt-0.5">{value}</div>
      </div>
    </div>
  );
}

function SectionTitle({ children }) {
  return (
    <h2 className="font-serif text-2xl text-brand-ink mt-8 mb-4">{children}</h2>
  );
}

function UserTable({ rows, testIdPrefix, emptyLabel, showCompletions = false }) {
  if (!rows.length) {
    return (
      <div className="rounded-2xl border border-brand-linen bg-white p-6 text-center text-brand-muted text-sm mb-4">
        {emptyLabel}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-brand-linen bg-white overflow-hidden mb-4">
      <div className={`grid gap-3 border-b border-brand-linen bg-brand-linen/40 px-5 py-3 text-[11px] font-medium uppercase tracking-widest text-brand-muted ${showCompletions ? "grid-cols-12" : "grid-cols-10"}`}>
        <div className="col-span-4">Name</div>
        <div className="col-span-3">Registered</div>
        <div className="col-span-3">Last sign-in</div>
        {showCompletions && <div className="col-span-2 text-right">Completed</div>}
      </div>
      {rows.map((r) => (
        <div
          key={r.id}
          data-testid={`${testIdPrefix}-${r.id}`}
          className={`grid gap-3 border-b border-brand-linen last:border-b-0 px-5 py-3 text-sm hover:bg-brand-linen/20 transition-colors ${showCompletions ? "grid-cols-12" : "grid-cols-10"}`}
        >
          <div className="col-span-4 min-w-0">
            <div className="text-brand-ink font-medium truncate" title={r.name || ""}>{r.name || "—"}</div>
            <div className="text-brand-muted text-xs truncate flex items-center gap-1" title={r.email || ""}>
              <Mail className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
              {r.email || "—"}
            </div>
            {r.password_reset_at && (
              <div className="text-brand-muted text-[11px] mt-0.5 flex items-center gap-1">
                <KeyRound className="w-2.5 h-2.5 flex-shrink-0" strokeWidth={1.5} />
                pw reset {fmt(r.password_reset_at)}
              </div>
            )}
          </div>
          <div className="col-span-3 text-brand-secondary text-xs">
            <div>{fmt(r.created_at)}</div>
            {daysSince(r.created_at) !== null && (
              <div className="text-brand-muted">{daysSince(r.created_at)}d ago</div>
            )}
          </div>
          <div className="col-span-3 text-brand-secondary text-xs">
            <div className="flex items-center gap-1">
              <Clock className="w-3 h-3 flex-shrink-0" strokeWidth={1.5} />
              {fmt(r.last_login_at)}
            </div>
            {daysSince(r.last_login_at) !== null && (
              <div className="text-brand-muted">{daysSince(r.last_login_at)}d ago</div>
            )}
          </div>
          {showCompletions && (
            <div className="col-span-2 text-right text-brand-ink">
              {r.completed_courses ?? 0}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
